import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { VendorPlan } from '../our-vendors/models/vendor.model';
import { OfferDraft, OfferEditor, defaultOffer, offerPayload } from './OfferEditor';

/**
 * Approve a signup, and decide its prices at the same moment
 * (docs/pricing-v2.md, Offers): how long the trial runs, and which plans at
 * which price the vendor will see when choosing. Prices are not fixed; this
 * is where each inbound vendor gets its own.
 */
export function ApproveSignupDialog({
  open,
  onOpenChange,
  domain,
  busy,
  onApprove,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  domain?: string;
  busy?: boolean;
  onApprove: (body: { note?: string; trialDays?: number; offer?: unknown }) => void;
}) {
  const { t } = useTranslation();
  const plansApi = usePlansApi();
  const [plans, setPlans] = useState<VendorPlan[]>([]);
  const [trialDays, setTrialDays] = useState<number | ''>(14);
  const [withOffer, setWithOffer] = useState(true);
  const [offer, setOffer] = useState<OfferDraft | null>(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) return;
    Promise.resolve(plansApi.getPlans())
      .then((rows) => {
        const list = (rows as VendorPlan[]) || [];
        setPlans(list);
        const trial = list.find((p) => p.pricingType === 'trial' || p.slug === 'trial');
        setTrialDays(trial?.trialDays || 14);
        setOffer(defaultOffer(list));
      })
      .catch(() => setPlans([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const payload = withOffer && offer ? offerPayload(offer) : null;
  const invalid = withOffer && !payload;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[10000] max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle>{t('PRICING.APPROVE.TITLE', { domain: domain || '' })}</DialogTitle>
          <DialogDescription>{t('PRICING.APPROVE.SUBTITLE')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="approve-trial" className="text-xs text-muted-foreground">{t('PRICING.APPROVE.TRIAL_DAYS')}</Label>
            <Input id="approve-trial" type="number" min={1} max={90} className="w-32 tabular-nums" value={trialDays}
              onChange={(e) => setTrialDays(e.target.value === '' ? '' : Number(e.target.value))} data-testid="approve-trial-days" />
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t('PRICING.APPROVE.OFFER')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('PRICING.APPROVE.OFFER_HELP')}</p>
              </div>
              <Switch checked={withOffer} onCheckedChange={setWithOffer} aria-label={t('PRICING.APPROVE.OFFER')} data-testid="approve-with-offer" />
            </div>
            {withOffer && offer && <OfferEditor plans={plans} value={offer} onChange={setOffer} />}
            {!withOffer && <p className="text-xs text-muted-foreground">{t('PRICING.APPROVE.NO_OFFER')}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="approve-note" className="text-xs text-muted-foreground">{t('PRICING.APPROVE.NOTE')}</Label>
            <Textarea id="approve-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" className="text-muted-foreground" onClick={() => onOpenChange(false)}>{t('COMMON.CANCEL', 'Cancel')}</Button>
          <Button variant="outline" disabled={busy || invalid} data-testid="approve-confirm"
            onClick={() => onApprove({ note: note || undefined, trialDays: Number(trialDays) || undefined, ...(payload ? { offer: payload } : {}) })}>
            {t('PRICING.APPROVE.CONFIRM')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
